import { Bell, Activity, Settings, CalendarDays, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HEADER } from "@/constants/testIds";
import { useWeekSettings } from "@/context/WeekSettingsContext";
import { useState } from "react";
import { UserMenu } from "./UserMenu";
import { AlvysRefresh } from "./AlvysRefresh";
import { useApiBusy } from "@/lib/swr";

export default function Header() {
  const refreshing = useApiBusy();
  const { weekStartDay, excludeUndelivered, settingsOpen, setSettingsOpen, applySettings, DAY_NAMES } = useWeekSettings();
  const [tempDay, setTempDay] = useState(weekStartDay);
  const [tempExclude, setTempExclude] = useState(excludeUndelivered);

  const openSettings = () => {
    setTempDay(weekStartDay);
    setTempExclude(excludeUndelivered);
    setSettingsOpen(true);
  };

  return (
    <>
      <header
        data-testid={HEADER.root}
        className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-800/60 bg-[#0B1120]/95 backdrop-blur-xl px-6"
      >
        {/* Left: telemetry badge */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3 py-1">
            <Activity className="h-3.5 w-3.5 text-emerald-400 animate-pulse-dot" />
            <span className="text-xs font-semibold text-emerald-400 tracking-wide">LIVE</span>
          </div>
          {refreshing && <span data-testid="header-refreshing" className="inline-flex items-center gap-1.5 text-[11px] text-slate-400"><RefreshCw className="h-3 w-3 animate-spin" />Refreshing…</span>}
        </div>

        <div className="flex-1" />

        {/* Right: week badge + settings + notifications */}
        <div className="flex items-center gap-2">
          <AlvysRefresh />
          <button
            data-testid="week-settings-btn"
            onClick={openSettings}
            className="flex items-center gap-1.5 rounded-lg bg-slate-800/60 border border-slate-700/50 px-3 py-1.5 hover:bg-slate-700/60 transition"
          >
            <CalendarDays className="h-3.5 w-3.5 text-blue-400" />
            <span className="text-[11px] text-slate-300 font-semibold">{DAY_NAMES[weekStartDay]} → {DAY_NAMES[weekStartDay]}</span>
            {excludeUndelivered && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1 rounded">Delivered only</span>}
          </button>
          <Button
            data-testid={HEADER.notifications}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
          >
            <Bell className="h-4 w-4" />
          </Button>
          <UserMenu />
        </div>
      </header>

      {/* Week Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="week-settings-dialog" className="bg-slate-900 border-slate-700 text-slate-200 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-400" />
              Week Settings
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-400">
              Configure the working week boundaries for Alvys data grouping.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-400 mb-2 block">Week starts on</Label>
              <Select value={String(tempDay)} onValueChange={v => setTempDay(Number(v))}>
                <SelectTrigger data-testid="week-start-day-select" className="bg-slate-800/50 border-slate-700/50 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {DAY_NAMES.map((name, i) => (
                    <SelectItem key={i} value={String(i)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-2">
                The working week runs from the selected day 00:00 US Central time through the following 6 days.
              </p>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/40 border border-slate-800">
              <Checkbox
                id="exclude-undelivered"
                data-testid="exclude-undelivered-checkbox"
                checked={tempExclude}
                onCheckedChange={setTempExclude}
                className="mt-0.5 border-slate-600 data-[state=checked]:bg-blue-600"
              />
              <label htmlFor="exclude-undelivered" className="cursor-pointer">
                <p className="text-xs font-semibold text-slate-200">Exclude undelivered loads</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Undelivered loads are excluded. Pickup on {DAY_NAMES[tempDay]} starts a week; delivery on {DAY_NAMES[tempDay]} (any time, CT) closes it. Loads delivered after the closing {DAY_NAMES[tempDay]} move to the week they were delivered.
                </p>
              </label>
            </div>

            <div className="rounded-lg bg-slate-800/40 border border-slate-800 p-3">
              <p className="text-xs text-slate-400">
                <span className="font-semibold text-slate-300">Current setting:</span> {DAY_NAMES[tempDay]} → {DAY_NAMES[tempDay]}
                {tempExclude && <span className="text-amber-400 ml-2">(delivered only)</span>}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)} className="text-slate-400 hover:text-slate-200">Cancel</Button>
            <Button data-testid="save-week-settings-btn" onClick={() => applySettings(tempDay, tempExclude)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              Apply Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
